/**
 * CR310: S0 Scan Settings v2 - Config Hook
 * Handles config loading, saving, dirty detection, and reset
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  type BatchRunFlyerConfig,
  type OcrEngine,
  type AiScanModel,
  type PaddleOcrConfig,
  type PromptPackOption,
  type ExecutionConfig,
  type FlyerScanDefaults,
  type S12OcrConfig,
  type S12FallbackOcrConfig,
  type FlyerCaptureMode,
  type SnippetRefinementConfig,
  DEFAULT_EXECUTION_CONFIG,
  DEFAULT_FLYER_SCAN_DEFAULTS,
  DEFAULT_S12_OCR_CONFIG,
  DEFAULT_S12_FALLBACK_CONFIG,
  DEFAULT_FLYER_CAPTURE_MODE,
  DEFAULT_SNIPPET_REFINEMENT_CONFIG,
  type LargePagesConfig,
  DEFAULT_SIGNALS,
  DEFAULT_PADDLE_OCR_CONFIG,
  DEFAULT_LARGE_PAGES_CONFIG,
} from './types';
import {
  DEFAULT_DEAL_LOCATOR_CONFIG,
  DEFAULT_TILED_SCANNING_CONFIG,
  DEFAULT_EXTRACTION_FOCUS_CONFIG,
} from '@/lib/flyer-pipeline-v2/dealLocatorTypes';
import type { CandidateSignal } from '@/lib/flyer-pipeline-v2/types';
import type {
  DealLocatorConfig,
  TiledScanningConfig,
  ExtractionFocusConfig,
} from '@/lib/flyer-pipeline-v2/dealLocatorTypes';
import { normalizeOcrEngine } from '@/lib/batch-runs/ocrEngine';

// Full default config
const DEFAULT_CONFIG: BatchRunFlyerConfig = {
  ocrEngine: 'tesseract',
  minOcrScore: 25,
  paddleOcr: DEFAULT_PADDLE_OCR_CONFIG,
  largePages: DEFAULT_LARGE_PAGES_CONFIG,
  candidateThreshold: 12,
  signals: DEFAULT_SIGNALS,
  dealLocator: DEFAULT_DEAL_LOCATOR_CONFIG,
  tiledScanning: DEFAULT_TILED_SCANNING_CONFIG,
  extractionFocus: DEFAULT_EXTRACTION_FOCUS_CONFIG,
  aiEnabled: true,
  aiModel: 'google/gemini-3-flash-preview',
  maxAiCalls: 50,
  maxPagesPerFlyer: 8,
  maxTotalPages: 20,
  aiTimeoutSeconds: 90, // CR310: Now in seconds
  promptPackId: null,
  inputMode: 'full_page',
  execution: DEFAULT_EXECUTION_CONFIG, // CR403: Execution defaults
  flyerDefaults: DEFAULT_FLYER_SCAN_DEFAULTS, // CR405: Flyer scan defaults
  s12Ocr: DEFAULT_S12_OCR_CONFIG, // CR404: S1.2 OCR watchdog + fallback
  flyerCaptureMode: DEFAULT_FLYER_CAPTURE_MODE, // CR384
  renderConcurrency: 3, // CR414
  ocrConcurrency: 3, // CR414
  snippetRefinement: DEFAULT_SNIPPET_REFINEMENT_CONFIG, // CR555
};

// Deep compare function for dirty detection
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return a === b;
  
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }
  
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  
  const aKeys = Object.keys(a as object);
  const bKeys = Object.keys(b as object);
  
  if (aKeys.length !== bKeys.length) return false;
  
  return aKeys.every(key => 
    bKeys.includes(key) && 
    deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
  );
}

// Parse config from DB format to our internal format
function parseConfigFromDb(flyerConfig: Record<string, unknown>): BatchRunFlyerConfig {
  // Normalize aliases so S0 value is canonical and stable across all pipeline views.
  const ocrEngine = normalizeOcrEngine(flyerConfig.ocrEngine) as OcrEngine;

  // CR310: Handle legacy aiCallTimeoutMs -> aiTimeoutSeconds
  let aiTimeoutSeconds = DEFAULT_CONFIG.aiTimeoutSeconds;
  if (flyerConfig.aiTimeoutSeconds !== undefined) {
    aiTimeoutSeconds = flyerConfig.aiTimeoutSeconds as number;
  } else if (flyerConfig.aiCallTimeoutMs !== undefined) {
    aiTimeoutSeconds = Math.round((flyerConfig.aiCallTimeoutMs as number) / 1000);
  }

  // CR310: Parse paddleOcr config
  const paddleOcr: PaddleOcrConfig = {
    ...DEFAULT_PADDLE_OCR_CONFIG,
    ...(flyerConfig.paddleOcr as Partial<PaddleOcrConfig> | undefined),
  };

  // CR317: Parse largePages config with deep merge
  const largePages: LargePagesConfig = {
    downscale: {
      ...DEFAULT_LARGE_PAGES_CONFIG.downscale,
      ...(flyerConfig.largePages as Partial<LargePagesConfig> | undefined)?.downscale,
      applyWhen: {
        ...DEFAULT_LARGE_PAGES_CONFIG.downscale.applyWhen,
        ...(flyerConfig.largePages as Partial<LargePagesConfig> | undefined)?.downscale?.applyWhen,
      },
    },
    tiling: {
      ...DEFAULT_LARGE_PAGES_CONFIG.tiling,
      ...(flyerConfig.largePages as Partial<LargePagesConfig> | undefined)?.tiling,
      applyWhen: {
        ...DEFAULT_LARGE_PAGES_CONFIG.tiling.applyWhen,
        ...(flyerConfig.largePages as Partial<LargePagesConfig> | undefined)?.tiling?.applyWhen,
      },
    },
  };

  // CR403: Parse execution config (can come from flyerConfig or separate execution key)
  const executionConfig: ExecutionConfig = {
    timeoutSeconds: (flyerConfig.execution as Partial<ExecutionConfig>)?.timeoutSeconds 
      ?? DEFAULT_EXECUTION_CONFIG.timeoutSeconds,
    maxConcurrentScans: (flyerConfig.execution as Partial<ExecutionConfig>)?.maxConcurrentScans 
      ?? DEFAULT_EXECUTION_CONFIG.maxConcurrentScans,
  };

  // CR405: Parse flyer scan defaults
  const flyerDefaults: FlyerScanDefaults = {
    maxPagesPerFlyer: (flyerConfig.flyerDefaults as Partial<FlyerScanDefaults>)?.maxPagesPerFlyer 
      ?? DEFAULT_FLYER_SCAN_DEFAULTS.maxPagesPerFlyer,
    maxTotalPagesPerRun: (flyerConfig.flyerDefaults as Partial<FlyerScanDefaults>)?.maxTotalPagesPerRun 
      ?? DEFAULT_FLYER_SCAN_DEFAULTS.maxTotalPagesPerRun,
  };

  // CR404: Parse S1.2 OCR config (watchdog + fallback)
  const rawS12 = flyerConfig.s12Ocr as Partial<S12OcrConfig> | undefined;
  const rawFallback = rawS12?.fallback as Partial<S12FallbackOcrConfig> | undefined;
  
  const s12Ocr: S12OcrConfig = {
    watchdogTimeoutSec: rawS12?.watchdogTimeoutSec ?? DEFAULT_S12_OCR_CONFIG.watchdogTimeoutSec,
    ocrCompletionMaxTokens: rawS12?.ocrCompletionMaxTokens ?? DEFAULT_S12_OCR_CONFIG.ocrCompletionMaxTokens,
    useFanOut: rawS12?.useFanOut ?? DEFAULT_S12_OCR_CONFIG.useFanOut,
    fanOutMinPages: rawS12?.fanOutMinPages ?? DEFAULT_S12_OCR_CONFIG.fanOutMinPages,
    fanOutConcurrency: rawS12?.fanOutConcurrency ?? DEFAULT_S12_OCR_CONFIG.fanOutConcurrency,
    fallback: {
      enabled: rawFallback?.enabled ?? DEFAULT_S12_FALLBACK_CONFIG.enabled,
      trigger: rawFallback?.trigger ?? DEFAULT_S12_FALLBACK_CONFIG.trigger,
      timeoutSeconds: rawFallback?.timeoutSeconds ?? DEFAULT_S12_FALLBACK_CONFIG.timeoutSeconds,
      engineMode: rawFallback?.engineMode ?? DEFAULT_S12_FALLBACK_CONFIG.engineMode,
      engine: rawFallback?.engine,
      renderDpi: rawFallback?.renderDpi ?? DEFAULT_S12_FALLBACK_CONFIG.renderDpi,
      maxSidePx: rawFallback?.maxSidePx ?? DEFAULT_S12_FALLBACK_CONFIG.maxSidePx,
      downscaleFactor: rawFallback?.downscaleFactor ?? DEFAULT_S12_FALLBACK_CONFIG.downscaleFactor,
      colorMode: rawFallback?.colorMode ?? DEFAULT_S12_FALLBACK_CONFIG.colorMode,
      binarizeEnabled: rawFallback?.binarizeEnabled ?? DEFAULT_S12_FALLBACK_CONFIG.binarizeEnabled,
      denoiseEnabled: rawFallback?.denoiseEnabled ?? DEFAULT_S12_FALLBACK_CONFIG.denoiseEnabled,
    },
  };

  return {
    ocrEngine,
    minOcrScore: (flyerConfig.minOcrScore as number) ?? DEFAULT_CONFIG.minOcrScore,
    paddleOcr,
    largePages,
    candidateThreshold: (flyerConfig.candidateThreshold as number) ?? DEFAULT_CONFIG.candidateThreshold,
    signals: (flyerConfig.signals as CandidateSignal[]) || DEFAULT_SIGNALS,
    dealLocator: (flyerConfig.dealLocator as DealLocatorConfig) || DEFAULT_DEAL_LOCATOR_CONFIG,
    tiledScanning: (flyerConfig.tiledScanning as TiledScanningConfig) || DEFAULT_TILED_SCANNING_CONFIG,
    extractionFocus: (flyerConfig.extractionFocus as ExtractionFocusConfig) || DEFAULT_EXTRACTION_FOCUS_CONFIG,
    aiEnabled: flyerConfig.aiEnabled !== false,
    aiModel: (flyerConfig.aiModel as AiScanModel) || DEFAULT_CONFIG.aiModel,
    maxAiCalls: (flyerConfig.maxAiCalls as number) ?? DEFAULT_CONFIG.maxAiCalls,
    maxPagesPerFlyer: (flyerConfig.maxPagesPerFlyer as number) ?? DEFAULT_CONFIG.maxPagesPerFlyer,
    maxTotalPages: (flyerConfig.maxTotalPages as number) ?? DEFAULT_CONFIG.maxTotalPages,
    aiTimeoutSeconds,
    promptPackId: (flyerConfig.promptPackId as string | null) ?? null,
    inputMode: (flyerConfig.inputMode as 'full_page' | 'crops_only' | 'both') || 'full_page',
    execution: executionConfig,
    flyerDefaults, // CR405
    s12Ocr, // CR404
    flyerCaptureMode: (flyerConfig.flyerCaptureMode as FlyerCaptureMode) || DEFAULT_FLYER_CAPTURE_MODE, // CR384
    renderConcurrency: (flyerConfig.renderConcurrency as number) ?? 3, // CR414
    ocrConcurrency: (flyerConfig.ocrConcurrency as number) ?? 3, // CR414
    snippetRefinement: {
      ...DEFAULT_SNIPPET_REFINEMENT_CONFIG,
      ...(flyerConfig.snippetRefinement as Partial<SnippetRefinementConfig> | undefined),
    }, // CR555
  };
}

// Convert config to DB format
function configToDbFormat(config: BatchRunFlyerConfig): Record<string, unknown> {
  return {
    ocrEngine: config.ocrEngine,
    minOcrScore: config.minOcrScore,
    paddleOcr: config.paddleOcr,
    largePages: config.largePages,
    candidateThreshold: config.candidateThreshold,
    signals: config.signals,
    dealLocator: config.dealLocator,
    tiledScanning: config.tiledScanning,
    extractionFocus: config.extractionFocus,
    aiEnabled: config.aiEnabled,
    aiModel: config.aiModel,
    maxAiCalls: config.maxAiCalls,
    maxPagesPerFlyer: config.maxPagesPerFlyer,
    maxTotalPages: config.maxTotalPages,
    aiTimeoutSeconds: config.aiTimeoutSeconds,
    promptPackId: config.promptPackId,
    inputMode: config.inputMode,
    execution: config.execution, // CR403: Persist execution settings
    flyerDefaults: config.flyerDefaults, // CR405: Persist flyer scan defaults
    s12Ocr: config.s12Ocr, // CR404: Persist S1.2 OCR watchdog + fallback
    flyerCaptureMode: config.flyerCaptureMode, // CR384
    renderConcurrency: config.renderConcurrency, // CR414
    ocrConcurrency: config.ocrConcurrency, // CR414
    snippetRefinement: config.snippetRefinement, // CR555
  };
}

interface UseS0ConfigOptions {
  batchRunId: string;
  currentConfig?: Record<string, unknown>;
  onConfigSaved?: () => void;
  disabled?: boolean;
}

export function useS0Config({ batchRunId, currentConfig, onConfigSaved, disabled }: UseS0ConfigOptions) {
  const { toast } = useToast();
  
  // Draft config (user's current edits)
  const [draftConfig, setDraftConfig] = useState<BatchRunFlyerConfig>(DEFAULT_CONFIG);
  // Last saved config (what's in the DB)
  const [lastSavedConfig, setLastSavedConfig] = useState<BatchRunFlyerConfig>(DEFAULT_CONFIG);
  const hasInitializedRef = useRef(false);
  const draftConfigRef = useRef<BatchRunFlyerConfig>(DEFAULT_CONFIG);
  const lastSavedConfigRef = useRef<BatchRunFlyerConfig>(DEFAULT_CONFIG);
  
  const [isSaving, setIsSaving] = useState(false);
  const [promptPacks, setPromptPacks] = useState<PromptPackOption[]>([]);
  const [loadingPacks, setLoadingPacks] = useState(false);

  useEffect(() => {
    draftConfigRef.current = draftConfig;
  }, [draftConfig]);

  useEffect(() => {
    lastSavedConfigRef.current = lastSavedConfig;
  }, [lastSavedConfig]);

  // Load config from batch run
  // CR415: Read from ocr.flyer.* first, fallback to flyerExtraction.*
  useEffect(() => {
    const hasUnsavedEdits = !deepEqual(draftConfigRef.current, lastSavedConfigRef.current);
    const applyHydratedConfig = (next: BatchRunFlyerConfig) => {
      setDraftConfig(next);
      setLastSavedConfig(next);
      hasInitializedRef.current = true;
    };

    if (currentConfig) {
      const flyerConfig = currentConfig.flyerExtraction as Record<string, unknown> | undefined;
      const ocrFlyerConfig = (currentConfig.ocr as Record<string, unknown> | undefined)?.flyer as Record<string, unknown> | undefined;
      
      if (flyerConfig || ocrFlyerConfig) {
        // CR415: Merge — prefer ocr.flyer.* keys over flyerExtraction.* for concurrency
        const baseConfig = flyerConfig || {};
        const baseS12 = (baseConfig.s12Ocr as Record<string, unknown> | undefined) || {};
        const mergedConfig = {
          ...baseConfig,
          // CR415: Concurrency from ocr.flyer.* takes precedence
          ...(ocrFlyerConfig?.render_concurrency != null ? { renderConcurrency: ocrFlyerConfig.render_concurrency } : {}),
          ...(ocrFlyerConfig?.ocr_concurrency != null ? { ocrConcurrency: ocrFlyerConfig.ocr_concurrency } : {}),
          s12Ocr: {
            ...baseS12,
            ...(ocrFlyerConfig?.ocr_completion_max_tokens != null ? { ocrCompletionMaxTokens: ocrFlyerConfig.ocr_completion_max_tokens } : {}),
            ...(ocrFlyerConfig?.use_fan_out != null ? { useFanOut: ocrFlyerConfig.use_fan_out } : {}),
            ...(ocrFlyerConfig?.fan_out_min_pages != null ? { fanOutMinPages: ocrFlyerConfig.fan_out_min_pages } : {}),
            ...(ocrFlyerConfig?.fan_out_concurrency != null ? { fanOutConcurrency: ocrFlyerConfig.fan_out_concurrency } : {}),
          },
        };
        const parsed = parseConfigFromDb(mergedConfig as Record<string, unknown>);
        if (!hasInitializedRef.current || !hasUnsavedEdits) {
          applyHydratedConfig(parsed);
        }
      } else {
        if (!hasInitializedRef.current || !hasUnsavedEdits) {
          applyHydratedConfig({ ...DEFAULT_CONFIG });
        }
      }
    } else {
      if (!hasInitializedRef.current || !hasUnsavedEdits) {
        applyHydratedConfig({ ...DEFAULT_CONFIG });
      }
    }
  }, [currentConfig]);

  // Fetch prompt packs
  useEffect(() => {
    const fetchPromptPacks = async () => {
      setLoadingPacks(true);
      try {
        const { data, error } = await supabase
          .from("prompt_packs")
          .select("id, name, version, is_active")
          .eq("domain", "gift_card_deals")
          .is("deleted_at", null)
          .order("updated_at", { ascending: false });

        if (error) throw error;
        setPromptPacks((data || []) as PromptPackOption[]);
      } catch (err) {
        console.error("Failed to fetch prompt packs:", err);
      } finally {
        setLoadingPacks(false);
      }
    };
    fetchPromptPacks();
  }, []);

  // Compute dirty state
  const isDirty = useMemo(() => {
    return !deepEqual(draftConfig, lastSavedConfig);
  }, [draftConfig, lastSavedConfig]);

  // Update a single config field
  const updateConfig = useCallback(<K extends keyof BatchRunFlyerConfig>(
    key: K, 
    value: BatchRunFlyerConfig[K]
  ) => {
    setDraftConfig(prev => ({ ...prev, [key]: value }));
  }, []);

  // Update paddle config
  const updatePaddleConfig = useCallback(<K extends keyof PaddleOcrConfig>(
    key: K,
    value: PaddleOcrConfig[K]
  ) => {
    setDraftConfig(prev => ({
      ...prev,
      paddleOcr: { ...prev.paddleOcr, [key]: value }
    }));
  }, []);

  // CR403: Update execution config
  const updateExecutionConfig = useCallback(<K extends keyof ExecutionConfig>(
    key: K,
    value: ExecutionConfig[K]
  ) => {
    setDraftConfig(prev => ({
      ...prev,
      execution: { ...prev.execution, [key]: value }
    }));
  }, []);

  // CR405: Update flyer scan defaults
  const updateFlyerDefaults = useCallback(<K extends keyof FlyerScanDefaults>(
    key: K,
    value: FlyerScanDefaults[K]
  ) => {
    setDraftConfig(prev => ({
      ...prev,
      flyerDefaults: { ...prev.flyerDefaults, [key]: value }
    }));
  }, []);

  // CR404: Update S1.2 OCR config (watchdog timeout)
  const updateS12OcrConfig = useCallback(<K extends keyof S12OcrConfig>(
    key: K,
    value: S12OcrConfig[K]
  ) => {
    setDraftConfig(prev => ({
      ...prev,
      s12Ocr: { ...prev.s12Ocr, [key]: value }
    }));
  }, []);

  // CR404: Update S1.2 Fallback OCR config
  const updateS12FallbackConfig = useCallback(<K extends keyof S12FallbackOcrConfig>(
    key: K,
    value: S12FallbackOcrConfig[K]
  ) => {
    setDraftConfig(prev => ({
      ...prev,
      s12Ocr: {
        ...prev.s12Ocr,
        fallback: { ...prev.s12Ocr.fallback, [key]: value }
      }
    }));
  }, []);

  // CR555: Update snippet refinement config
  const updateSnippetRefinement = useCallback(<K extends keyof SnippetRefinementConfig>(
    key: K,
    value: SnippetRefinementConfig[K]
  ) => {
    setDraftConfig(prev => ({
      ...prev,
      snippetRefinement: { ...prev.snippetRefinement, [key]: value }
    }));
  }, []);

  // Save config to DB
  const handleSave = useCallback(async () => {
    if (disabled) return;
    
    setIsSaving(true);
    try {
      const { data: batchRun, error: fetchError } = await supabase
        .from('cr075_batch_runs')
        .select('config_json')
        .eq('id', batchRunId)
        .single();

      if (fetchError) throw fetchError;

      const existingConfig = (batchRun?.config_json as Record<string, unknown>) || {};
      // CR415: Write concurrency to config_json.ocr.flyer.* (stable key path)
      const existingOcr = (existingConfig.ocr as Record<string, unknown>) || {};
      const newConfig = {
        ...existingConfig,
        flyerExtraction: JSON.parse(JSON.stringify(configToDbFormat(draftConfig))),
        ocr: {
          ...existingOcr,
          flyer: {
            render_concurrency: draftConfig.renderConcurrency,
            ocr_concurrency: draftConfig.ocrConcurrency,
            ocr_completion_max_tokens: draftConfig.s12Ocr.ocrCompletionMaxTokens,
            use_fan_out: draftConfig.s12Ocr.useFanOut,
            fan_out_min_pages: draftConfig.s12Ocr.fanOutMinPages,
            fan_out_concurrency: draftConfig.s12Ocr.fanOutConcurrency,
          },
        },
      };

      const { error: updateError } = await supabase
        .from('cr075_batch_runs')
        .update({ config_json: JSON.parse(JSON.stringify(newConfig)) })
        .eq('id', batchRunId);

      if (updateError) throw updateError;

      setLastSavedConfig(draftConfig);
      toast({
        title: 'Saved scan configuration',
        description: 'Changes have been persisted to this batch run.',
      });
      onConfigSaved?.();
    } catch (err) {
      console.error('Failed to save config:', err);
      toast({
        title: 'Save Failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }, [batchRunId, draftConfig, disabled, onConfigSaved, toast]);

  // Reset to last saved config
  const handleReset = useCallback(() => {
    setDraftConfig(lastSavedConfig);
    toast({
      title: 'Reset to saved',
      description: 'Changes have been discarded.',
    });
  }, [lastSavedConfig, toast]);

  return {
    config: draftConfig,
    isDirty,
    isSaving,
    promptPacks,
    loadingPacks,
    updateConfig,
    updatePaddleConfig,
    updateExecutionConfig, // CR403
    updateFlyerDefaults, // CR405
    updateS12OcrConfig, // CR404
    updateS12FallbackConfig, // CR404
    updateSnippetRefinement, // CR555
    handleSave,
    handleReset,
  };
}

export { DEFAULT_CONFIG };
